import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { dummyP2PAds } from "../../../data/dummyData";
import "../Home/Homepage.css";
import "./P2PAdsPage.css";

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso || "N/A";
  }
};

const P2PAdsPage = () => {
  const navigate = useNavigate();
  const [ads] = useState(dummyP2PAds);
  const [filters, setFilters] = useState({
    side: "All",
    status: "All",
    onlineStatus: "All",
    fiatCurrency: "",
    quoteCurrency: "",
    userId: "",
    adUid: "",
    minPrice: "",
    maxPrice: "",
  });

  const updateFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  const resetFilters = () => {
    setFilters({
      side: "All",
      status: "All",
      onlineStatus: "All",
      fiatCurrency: "",
      quoteCurrency: "",
      userId: "",
      adUid: "",
      minPrice: "",
      maxPrice: "",
    });
  };

  const handleViewDetails = (ad) => {
    navigate("/dashboard/p2p-ad-details", { state: { adId: ad._id } });
  };

  const filteredAds = useMemo(() => {
    return ads.filter((a) => {
      if (filters.side !== "All" && (a.side || "").toLowerCase() !== filters.side.toLowerCase()) return false;
      if (filters.status !== "All" && (a.status || "").toLowerCase() !== filters.status.toLowerCase().replace("_", "")) return false;
      if (filters.onlineStatus !== "All") {
        const wantOnline = filters.onlineStatus.toLowerCase() === "online";
        if (wantOnline !== !!a.isOnline) return false;
      }
      if (filters.fiatCurrency && (a.fiatCurrency || "").toLowerCase().indexOf(filters.fiatCurrency.toLowerCase()) === -1) return false;
      if (filters.quoteCurrency && (a.qouteCurrency || "").toLowerCase().indexOf(filters.quoteCurrency.toLowerCase()) === -1) return false;
      if (filters.userId) {
        const name = a.userInfo ? `${a.userInfo.firstName || ""} ${a.userInfo.lastName || ""}`.trim() || a.userInfo.emailId || "" : "";
        if (!name.toLowerCase().includes(filters.userId.toLowerCase()) && !(a.userInfo?.emailId || "").toLowerCase().includes(filters.userId.toLowerCase())) return false;
      }
      if (filters.adUid && (a.adUid || "").toLowerCase().indexOf(filters.adUid.toLowerCase()) === -1) return false;
      if (filters.minPrice !== "" && Number(a.fixedPrice) < Number(filters.minPrice)) return false;
      if (filters.maxPrice !== "" && Number(a.fixedPrice) > Number(filters.maxPrice)) return false;
      return true;
    });
  }, [ads, filters]);

  const counts = useMemo(() => ({
    total: ads.length,
    open: ads.filter((a) => (a.status || "").toUpperCase() === "OPEN").length,
    closed: ads.filter((a) => (a.status || "").toUpperCase() === "CLOSED").length,
    online: ads.filter((a) => !!a.isOnline).length,
  }), [ads]);

  return (
    <main className="dashboard-main dashboard-subpage p2p-ads-page">
      <section className="dashboard-hero p2p-ads-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>P2P Ads</span>
          </p>
          <h1 className="dashboard-hero__title">P2P Ads Management</h1>
          <p className="dashboard-hero__subtitle">Manage and monitor all P2P trading advertisements.</p>
        </div>
        <div className="p2p-ads-hero__icon">
          <span className="p2p-ads-hero__icon-text">Ad</span>
          <i className="fas fa-bullhorn p2p-ads-hero__icon-megaphone" />
        </div>
      </section>

      <div className="dashboard-body">
        <div className="p2p-ads-stats">
          <div className="p2p-ads-stat-card">
            <i className="fas fa-list p2p-ads-stat-card__icon p2p-ads-stat-card__icon--default" />
            <span className="p2p-ads-stat-card__value">{counts.total}</span>
            <span className="p2p-ads-stat-card__label">Total Ads</span>
          </div>
          <div className="p2p-ads-stat-card">
            <i className="fas fa-check-circle p2p-ads-stat-card__icon p2p-ads-stat-card__icon--open" />
            <span className="p2p-ads-stat-card__value">{counts.open}</span>
            <span className="p2p-ads-stat-card__label">Open</span>
          </div>
          <div className="p2p-ads-stat-card">
            <i className="fas fa-times-circle p2p-ads-stat-card__icon p2p-ads-stat-card__icon--closed" />
            <span className="p2p-ads-stat-card__value">{counts.closed}</span>
            <span className="p2p-ads-stat-card__label">Closed</span>
          </div>
          <div className="p2p-ads-stat-card">
            <i className="fas fa-wifi p2p-ads-stat-card__icon p2p-ads-stat-card__icon--online" />
            <span className="p2p-ads-stat-card__value">{counts.online}</span>
            <span className="p2p-ads-stat-card__label">Online</span>
          </div>
        </div>

        <section className="p2p-ads-filters-section">
          <h2 className="p2p-ads-filters__title"><i className="fas fa-filter" /> Filters</h2>
          <div className="p2p-ads-filters-grid">
            <div className="p2p-ads-filter-group">
              <label>Side</label>
              <select value={filters.side} onChange={(e) => updateFilter("side", e.target.value)}>
                <option value="All">All</option>
                <option value="BUY">BUY</option>
                <option value="SELL">SELL</option>
              </select>
            </div>
            <div className="p2p-ads-filter-group">
              <label>Status</label>
              <select value={filters.status} onChange={(e) => updateFilter("status", e.target.value)}>
                <option value="All">All</option>
                <option value="OPEN">OPEN</option>
                <option value="CLOSED">CLOSED</option>
                <option value="PARTIALLY_FILLED">PARTIALLY_FILLED</option>
              </select>
            </div>
            <div className="p2p-ads-filter-group">
              <label>Online Status</label>
              <select value={filters.onlineStatus} onChange={(e) => updateFilter("onlineStatus", e.target.value)}>
                <option value="All">All</option>
                <option value="Online">Online</option>
                <option value="Offline">Offline</option>
              </select>
            </div>
            <div className="p2p-ads-filter-group">
              <label>Fiat Currency</label>
              <input type="text" placeholder="e.g., USD" value={filters.fiatCurrency} onChange={(e) => updateFilter("fiatCurrency", e.target.value)} />
            </div>
            <div className="p2p-ads-filter-group">
              <label>Quote Currency</label>
              <input type="text" placeholder="e.g., BTC" value={filters.quoteCurrency} onChange={(e) => updateFilter("quoteCurrency", e.target.value)} />
            </div>
            <div className="p2p-ads-filter-group">
              <label>User ID</label>
              <input type="text" placeholder="User ID" value={filters.userId} onChange={(e) => updateFilter("userId", e.target.value)} />
            </div>
            <div className="p2p-ads-filter-group">
              <label>Ad UID</label>
              <input type="text" placeholder="Ad UID" value={filters.adUid} onChange={(e) => updateFilter("adUid", e.target.value)} />
            </div>
            <div className="p2p-ads-filter-group">
              <label>Min Price</label>
              <input type="text" placeholder="Min Price" value={filters.minPrice} onChange={(e) => updateFilter("minPrice", e.target.value)} />
            </div>
            <div className="p2p-ads-filter-group">
              <label>Max Price</label>
              <input type="text" placeholder="Max Price" value={filters.maxPrice} onChange={(e) => updateFilter("maxPrice", e.target.value)} />
            </div>
          </div>
          <div className="p2p-ads-filters-actions">
            <button type="button" className="p2p-ads-btn-reset" onClick={resetFilters}>
              <i className="fas fa-sync-alt" /> Reset Filters
            </button>
          </div>
        </section>

        <section className="p2p-ads-list-section">
          <h2 className="p2p-ads-list__title"><i className="fas fa-th-list" /> Ads List ({filteredAds.length})</h2>
          <div className="p2p-ads-table-wrap">
            <table className="p2p-ads-table">
              <thead>
                <tr>
                  <th>SR NO.</th>
                  <th>AD UID</th>
                  <th>SIDE</th>
                  <th>FIAT</th>
                  <th>QUOTE</th>
                  <th>PRICE</th>
                  <th>VOLUME</th>
                  <th>REMAINING</th>
                  <th>STATUS</th>
                  <th>ONLINE</th>
                  <th>USER</th>
                  <th>ORDERS</th>
                  <th>CREATED AT</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filteredAds.map((row, index) => (
                  <tr key={row._id}>
                    <td>{index + 1}</td>
                    <td>{row.adUid ?? "N/A"}</td>
                    <td><span className={`p2p-ads-side-badge p2p-ads-side-badge--${(row.side || "").toLowerCase()}`}>{row.side ?? "N/A"}</span></td>
                    <td>{row.fiatCurrency ?? "N/A"}</td>
                    <td>{row.qouteCurrency ?? "N/A"}</td>
                    <td>{row.fixedPrice ?? "N/A"}</td>
                    <td>{row.volume ?? "N/A"}</td>
                    <td>{row.remainingVolume != null && row.remainingVolume !== "" ? row.remainingVolume : "N/A"}</td>
                    <td><span className={`p2p-ads-status-badge p2p-ads-status-badge--${(row.status || "").toLowerCase().replace("_", "-")}`}>{row.status ?? "N/A"}</span></td>
                    <td><span className={`p2p-ads-online-badge p2p-ads-online-badge--${row.isOnline ? "yes" : "no"}`}>{row.isOnline ? "Yes" : "No"}</span></td>
                    <td>{row.userInfo ? `${(row.userInfo.firstName || "").trim()} ${(row.userInfo.lastName || "").trim()}`.trim() || row.userInfo.emailId || "N/A" : "N/A"}</td>
                    <td>{row.activeOrders != null && row.totalOrders != null ? `${row.activeOrders}/${row.totalOrders}` : "N/A"}</td>
                    <td>{row.createdAt ? formatDate(row.createdAt) : "N/A"}</td>
                    <td>
                      <button type="button" className="p2p-ads-btn-view" onClick={() => handleViewDetails(row)}>
                        <i className="fas fa-eye" /> View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </main>
  );
};

export default P2PAdsPage;
